/**
 * Client-side auto-formatting utility for AI assistant responses.
 * Wraps scannable data points in **bold** markdown so they render
 * as <strong> after the markdown→HTML pass.
 *
 * Runs BEFORE markdown-to-HTML conversion (operates on raw text).
 * Idempotent: already-bolded content is left untouched.
 */

/**
 * Check if a position in the text is inside an open bold span.
 * Counts ** markers before the offset; odd count means inside bold.
 */
function isInsideBold(text: string, offset: number): boolean {
  const prefix = text.substring(0, offset);
  const boldMarkers = prefix.match(/\*\*/g);
  return boldMarkers ? boldMarkers.length % 2 !== 0 : false;
}

/**
 * Check if a match is already wrapped in ** markers.
 */
function isAlreadyBolded(text: string, matchStart: number, matchEnd: number): boolean {
  const before = text.substring(Math.max(0, matchStart - 2), matchStart);
  const after = text.substring(matchEnd, matchEnd + 2);
  return before === '**' && after === '**';
}

/**
 * Apply regex-based bolding to text, processing matches right-to-left
 * so that insertions don't shift subsequent offsets.
 */
function applyBolding(text: string, regex: RegExp): string {
  // Collect all matches first
  const matches: Array<{ start: number; end: number; full: string }> = [];
  let m: RegExpExecArray | null;
  
  // Reset regex
  regex.lastIndex = 0;
  while ((m = regex.exec(text)) !== null) {
    matches.push({ start: m.index, end: m.index + m[0].length, full: m[0] });
  }

  // Process right-to-left to preserve offsets
  let result = text;
  for (let i = matches.length - 1; i >= 0; i--) {
    const { start, end, full } = matches[i];
    if (isAlreadyBolded(result, start, end)) continue;
    if (isInsideBold(result, start)) continue;
    result = result.substring(0, start) + `**${full}**` + result.substring(end);
  }

  return result;
}

/**
 * Auto-bold dollar amounts, percentages, and status keywords
 * that the AI model may have missed.
 */
export function smartFormat(text: string): string {
  if (!text) return text;

  let result = text;

  // 1. Dollar amounts:  $1,250  $45.99  $0
  result = applyBolding(result, /\$\d{1,3}(?:,\d{3})*(?:\.\d{1,2})?(?!\d)/g);

  // 2. Percentages:  85%  120%  3.5%
  result = applyBolding(result, /\d{1,3}(?:\.\d{1,2})?%/g);

  // 3. Status keywords (case-insensitive, word-boundary)
  const statusKeywords = [
    'at risk',
    'on track',
    'over budget',
    'under budget',
    'critically underfunded',
    'completed',
    'in progress',
    'not started',
    'on hold',
    'overdue',
  ];

  for (const kw of statusKeywords) {
    const escaped = kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`\\b${escaped}\\b`, 'gi');
    result = applyBolding(result, re);
  }

  // 4. Clean up any accidental double-bolding: ****text**** → **text**
  result = result.replace(/\*{4,}([^*]+)\*{4,}/g, '**$1**');

  return result;
}
